import { useEffect, useState, useRef } from 'react';
import { FREQ_OCUPADAS, FREQ_RESERVADAS, FREQ_LIBRES } from '@/mocks/frequencyCounters';

function AnimatedCounter({ value, className }: { value: number; className?: string }) {
  const [count, setCount] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated) {
          setHasAnimated(true);
        }
      },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [hasAnimated]);

  useEffect(() => {
    if (!hasAnimated) return;
    const duration = 2000;
    const steps = 50;
    const increment = value / steps;
    let current = 0;
    const timer = setInterval(() => {
      current += increment;
      if (current >= value) {
        setCount(value);
        clearInterval(timer);
      } else {
        setCount(Math.floor(current));
      }
    }, duration / steps);
    return () => clearInterval(timer);
  }, [hasAnimated, value]);

  return (
    <span ref={ref} className={className}>
      {new Intl.NumberFormat('es-ES').format(count)}
    </span>
  );
}

const exclusivityStats = [
  {
    value: FREQ_LIBRES,
    label: 'Disponibles',
    sublabel: 'Frecuencias libres para solicitar',
    icon: 'ri-radar-line',
    color: 'emerald',
    bgFrom: 'from-emerald-500/10',
    bgTo: 'to-emerald-500/5',
    border: 'border-emerald-500/20',
    iconBg: 'bg-emerald-500/10',
    iconColor: 'text-emerald-600',
    textColor: 'text-emerald-700',
    dotColor: 'bg-emerald-500',
  },
  {
    value: FREQ_OCUPADAS,
    label: 'Asignadas',
    sublabel: 'Radios activas en el dial',
    icon: 'ri-radio-line',
    color: 'amber',
    bgFrom: 'from-amber-500/10',
    bgTo: 'to-amber-500/5',
    border: 'border-amber-500/20',
    iconBg: 'bg-amber-500/10',
    iconColor: 'text-amber-600',
    textColor: 'text-amber-700',
    dotColor: 'bg-amber-500',
  },
  {
    value: FREQ_RESERVADAS,
    label: 'Reservadas',
    sublabel: 'En proceso de aprobación',
    icon: 'ri-time-line',
    color: 'rose',
    bgFrom: 'from-rose-500/10',
    bgTo: 'to-rose-500/5',
    border: 'border-rose-500/20',
    iconBg: 'bg-rose-500/10',
    iconColor: 'text-rose-600',
    textColor: 'text-rose-700',
    dotColor: 'bg-rose-500',
  },
];

const TOTAL_FREQ = 11300;
const TAKEN_FREQ = FREQ_OCUPADAS + FREQ_RESERVADAS;
const TAKEN_PCT = ((TAKEN_FREQ / TOTAL_FREQ) * 100).toFixed(1);

export default function ExclusivitySection() {
  return (
    <section id="exclusividad" className="relative py-20 md:py-28 overflow-hidden">
      {/* Background layer — slightly elevated so it stands out */}
      <div className="absolute inset-0 bg-background-100">
        <div className="absolute inset-0 bg-gradient-to-b from-background-50 via-transparent to-background-100"></div>
      </div>

      {/* Ambient glows */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-accent-500/5 rounded-full blur-3xl pointer-events-none"></div>
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-emerald-500/4 rounded-full blur-3xl pointer-events-none"></div>

      {/* Dot grid texture */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.03]" style={{
        backgroundImage: 'radial-gradient(circle, currentColor 1px, transparent 1px)',
        backgroundSize: '24px 24px',
      }}></div>

      <div className="relative z-10 w-full px-4 md:px-8 lg:px-12 max-w-7xl mx-auto">
        {/* ── Urgency badge ── */}
        <div className="flex justify-center mb-6">
          <div className="inline-flex items-center gap-2.5 px-5 py-2 rounded-full bg-foreground-950 text-background-50">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
            </span>
            <span className="text-xs font-bold tracking-wider uppercase">Acceso Limitado</span>
            <span className="text-background-50/30">|</span>
            <span className="text-xs text-background-50/70">Etapa de Lanzamiento</span>
          </div>
        </div>

        {/* ── Headline ── */}
        <div className="text-center mb-3">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-foreground-950 tracking-tight leading-[1.1]">
            Solo existen{' '}
            <span className="text-accent-600">11,300</span>
            {' '}Frecuencias RFW
          </h2>
        </div>

        {/* ── Scarcity visualization ── */}
        <div className="max-w-xl mx-auto mb-12">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-foreground-600">
              {TAKEN_FREQ} asignadas
            </span>
            <span className="text-xs font-semibold text-foreground-400">
              {FREQ_LIBRES.toLocaleString('es-ES')} disponibles
            </span>
          </div>
          <div className="relative h-3 bg-foreground-200/40 rounded-full overflow-hidden">
            <div
              className="absolute inset-y-0 left-0 bg-gradient-to-r from-accent-500 to-accent-400 rounded-full transition-all duration-[2s] ease-out"
              style={{ width: `${TAKEN_PCT}%` }}
            ></div>
            {/* Tick marks */}
            {[25, 50, 75].map((pct) => (
              <div
                key={pct}
                className="absolute top-0 bottom-0 w-px bg-background-50/60"
                style={{ left: `${pct}%` }}
              ></div>
            ))}
          </div>
          <p className="mt-2 text-center text-foreground-400 text-[11px] leading-relaxed">
            Solo el <strong className="text-foreground-600">{TAKEN_PCT}%</strong> del espectro está ocupado. El resto espera por nuevas radios.
          </p>
        </div>

        {/* ── Three stat cards ── */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 md:gap-6 max-w-3xl mx-auto mb-12">
          {exclusivityStats.map((stat) => (
            <div
              key={stat.label}
              className={`group relative rounded-2xl border ${stat.border} bg-gradient-to-br ${stat.bgFrom} ${stat.bgTo} p-6 md:p-7 text-center transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:shadow-${stat.color}-500/10`}
            >
              <div className={`w-12 h-12 mx-auto flex items-center justify-center rounded-xl ${stat.iconBg} ${stat.iconColor} mb-4 transition-all duration-300 group-hover:scale-110`}>
                <i className={`${stat.icon} text-lg`}></i>
              </div>
              <div className={`text-3xl md:text-4xl font-extrabold ${stat.textColor} tabular-nums mb-1`}>
                <AnimatedCounter value={stat.value} />
              </div>
              <p className="text-foreground-900 font-bold text-sm md:text-base mb-1">{stat.label}</p>
              <p className="text-foreground-500 text-[11px] md:text-xs leading-relaxed">{stat.sublabel}</p>
              <div className={`absolute top-3 right-3 w-2 h-2 rounded-full ${stat.dotColor}`}></div>
            </div>
          ))}
        </div>

        {/* ── Approval message ── */}
        <div className="max-w-lg mx-auto">
          <div className="relative rounded-2xl border border-foreground-200/60 bg-background-50/80 backdrop-blur-sm p-6 md:p-7 text-center">
            <div className="absolute -top-4 left-1/2 -translate-x-1/2">
              <div className="w-8 h-8 flex items-center justify-center rounded-full bg-foreground-950 text-background-50 shadow-lg">
                <i className="ri-shield-check-line text-sm"></i>
              </div>
            </div>
            <p className="mt-2 text-foreground-800 font-semibold text-sm md:text-base leading-relaxed">
              El acceso requiere aprobación.
            </p>
            <p className="mt-1 text-foreground-500 text-xs md:text-sm leading-relaxed">
              No todas las radios son aceptadas. Cada solicitud es evaluada para garantizar calidad, contenido original y compromiso con la comunidad.
            </p>
            <div className="mt-4 flex items-center justify-center gap-2">
              <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-50 border border-amber-200">
                <i className="ri-vip-crown-line text-amber-600 text-[10px]"></i>
                <span className="text-amber-700 text-[11px] font-semibold whitespace-nowrap">Proceso selectivo</span>
              </div>
              <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-200">
                <i className="ri-verified-badge-line text-emerald-600 text-[10px]"></i>
                <span className="text-emerald-700 text-[11px] font-semibold whitespace-nowrap">Calidad garantizada</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}